import { LitElement, html, css } from 'lit'

class Carousel extends LitElement {
  static styles = css`
    :host {
      display: block;
      position: relative;
    }

    .outer {
      overflow-x: auto;
      scrollbar-width: none;
      scroll-snap-type: x mandatory;
      margin-left: calc(var(--gutter) * -1);
      width: calc(100% + var(--gutter) * 2);
    }

    .outer::-webkit-scrollbar {
      -webkit-appearance: none;
      width: 0;
      height: 0;
    }

    .inner {
      display: flex;
    }

    .slider-container {
      width: 40%;
      height: 0;
      border-bottom: 1px dashed hsl(var(--gray-6));
      position: relative;
      margin: 2rem auto 0;
    }

    .slider {
      width: 50%;
      height: 0;
      border-bottom: 1px solid hsl(var(--gray-5));
      position: absolute;
      top: 0;
    }

    button {
      cursor: pointer;
      position: absolute;
      display: flex;
      background-color: hsl(var(--gray-200));
      flex-direction: column;
      align-items: center;
      justify-content: center;
      border: none;
      width: 32px;
      height: 32px;
      border-radius: 100%;
      --icon-width: var(--spacing-6);
      --icon-height: var(--spacing-6);
      --icon-stroke-width: 2;
      --icon-offset: 0;
    }

    button:focus-visible {
      outline: var(--outline);
      outline-offset: 3px;
    }

    button:hover {
      background-color: hsl(var(--gray-150));
    }

    button:active {
      background-color: hsl(var(--gray-9));
    }

    button[disabled],
    button[disabled]:hover {
      cursor: auto;
      background: transparent;
    }

    button.prev {
      bottom: 50%;
      left: -60px;
    }

    button.next {
      bottom: 50%;
      right: -60px;
    }

    button span {
      clip: rect(0 0 0 0);
      clip-path: inset(50%);
      height: 1px;
      overflow: hidden;
      position: absolute;
      white-space: nowrap;
      width: 1px;
    }

    :host(:not([navigation])) .slider-container,
    :host(:not([navigation])) .prev,
    :host(:not([navigation])) .next {
      display: none;
    }

    @media (max-width: 1262px) {
      button {
        --icon-stroke-width: 1;
      }

      button.prev {
        bottom: -15px;
        left: calc(30% - 32px - var(--spacing-6));
      }

      button.next {
        bottom: -15px;
        right: calc(30% - 32px - var(--spacing-6));
      }
    }
  `

  static properties = {
    visibleCount: { type: Number },
    leftDisabled: { type: Boolean },
    rightDisabled: { type: Boolean },
    positionPercentage: { type: Number },
    sliderWidth: { type: Number },
    nextLabel: { type: String },
    previousLabel: { type: String },
    navigation: { type: Boolean, reflect: true },
  }

  constructor() {
    super()
    this.leftDisabled = true
    this.rightDisabled = false
    this.nextLabel = 'Show next item'
    this.previousLabel = 'Show previous item'
    this.itemWidth = 0
    this.classList.remove('grid')
    this.classList.remove('grid--md')
    this.items = this.querySelectorAll('carousel-item')
    this.gap = 0
    this.offset = 0
    this.navigation = true
    setTimeout(() => this.init())
    window.addEventListener('resize', () => this.init())
  }

  firstUpdated(_changedProperties) {
    this.slider = this.shadowRoot.querySelector('.slider')
  }

  init() {
    const parentWidth = this.clientWidth
    const visibleCount = this.getVisibleCount(parentWidth)
    this.gap = this.convertRemToPixels(
      getComputedStyle(document.body).getPropertyValue('--gutter')
    )
    this.itemWidth =
      (parentWidth - this.gap * (visibleCount - 1)) / visibleCount
    this.items.forEach((item) => {
      item.style.minWidth = this.itemWidth + 'px'
      item.style.maxWidth = this.itemWidth + 'px'
    })

    //Set slider
    const outer = this.shadowRoot.querySelector('.outer')
    this.sliderWidth = (outer.clientWidth / outer.scrollWidth) * 100

    this.navigation = this.items.length > visibleCount
    
    this.shadowRoot.querySelector('.outer').scroll({
      top: 0,
      left: 0
    })
  }

  getVisibleCount(availableWidth) {
    if (availableWidth <= 360) return 1.1 // Hint next item on screen
    else if (availableWidth < 500) return 1.2
    else if (availableWidth < 768) return 2
    else return 3
  }

  handleScroll(e) {
    this.offset = e.target.scrollLeft
    const outer = this.shadowRoot.querySelector('.outer')

    this.leftDisabled = this.offset === 0
    this.rightDisabled = this.offset === outer.scrollWidth - outer.clientWidth

    // Update slider position
    this.slider.style.left = `${(this.offset / outer.scrollWidth) * 100}%`
    this.slider.style.width = `${this.sliderWidth}%`
  }

  move(direction) {
    let move = 0
    if (direction === 'left' && this.offset > 0) {
      move = -(this.itemWidth + this.gap)
    } else if (direction === 'right') {
      move = this.itemWidth + this.gap
    }

    this.shadowRoot.querySelector('.outer').scroll({
      top: 0,
      left: this.offset + move,
      behavior: 'smooth',
    })
  }

  render() {
    return html`
      <button
        class="prev"
        ?disabled="${this.leftDisabled}"
        @click="${() => this.move('left')}"
      >
        <span class="visually-hidden">${this.previousLabel}</span>
        <arrow-left></arrow-left>
      </button>
      <div class="outer" @scroll="${this.handleScroll}">
        <div class="inner">
          <slot></slot>
        </div>
      </div>
      <button
        class="next"
        ?disabled="${this.rightDisabled}"
        @click="${() => this.move('right')}"
      >
        <span class="visually-hidden">${this.nextLabel}</span>
        <arrow-right></arrow-right>
      </button>
      <div class="slider-container">
        <div class="slider"></div>
      </div>
    `
  }

  convertRemToPixels(rem) {
    return (
      parseFloat(rem) *
      parseFloat(getComputedStyle(document.documentElement).fontSize)
    )
  }
}

customElements.define('carousel-container', Carousel)

class CarouselItem extends LitElement {
  static styles = css`
    :host {
      display: block;
      box-sizing: content-box !important;
      scroll-snap-align: start;
      padding-left: var(--gutter) !important;
    }

    :host(:last-child) {
      padding-right: var(--gutter) !important;
    }
  `

  render() {
    return html` <slot></slot>`
  }
}

customElements.define('carousel-item', CarouselItem)
